import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Camera, FileSignature, FileText, Loader2, MessageSquare, Wallet } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { portalApi } from "@/components/portal/portalApi";
import { PortalMarco, SeccionPortal } from "@/components/portal/PortalMarco";
import { EstadoObra } from "@/components/portal/EstadoObra";
import { PresupuestoPortal } from "@/components/portal/PresupuestoPortal";
import { ResponderPresupuesto } from "@/components/portal/ResponderPresupuesto";
import { ContratosPortal } from "@/components/portal/ContratosPortal";
import { GaleriaPortal } from "@/components/portal/GaleriaPortal";
import { PagosPortal } from "@/components/portal/PagosPortal";
import { MensajesPortal } from "@/components/portal/MensajesPortal";

export default function PortalCliente() {
  const { token } = useParams();
  const api = useMemo(() => portalApi(token), [token]);
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [resp, setResp] = useState(null);
  const cargar = useCallback(() => api.http.get("").then((r) => setD(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Enlace no válido")), [api]);
  useEffect(() => { cargar(); }, [cargar]);

  const responder = async (motivo) => {
    const { accion, p } = resp;
    try { await api.http.post(`/presupuestos/${p.id}/${accion}`, accion === "rechazar" ? { motivo } : {}); toast.success(accion === "aprobar" ? "Presupuesto aprobado. Hemos avisado a la empresa." : "Respuesta enviada a la empresa."); setResp(null); cargar(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pdfPresupuesto = (p) => api.descargar(`/presupuestos/${p.id}/pdf`, `${p.numero}.pdf`).catch(() => toast.error("No se pudo descargar el PDF"));

  if (error) return <PortalMarco><div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="portal-error"><AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" /><p className="font-semibold text-[#1B2B4B]">{error}</p></div></PortalMarco>;
  if (!d) return <PortalMarco><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#00B4CC]" /></div></PortalMarco>;

  const secciones = [
    ["presupuestos", "Presupuestos", FileText, d.presupuestos.length],
    ["contratos", "Contratos", FileSignature, d.contratos.length],
    ["fotos", "Fotos", Camera, d.fotos.length],
    ["pagos", "Pagos", Wallet, d.pagos.facturas.length],
    ["mensajes", "Mensajes", MessageSquare, d.mensajes.length],
  ].filter((s) => s[3] > 0);
  const pendientes = d.presupuestos.filter((p) => p.estado_cliente === "pendiente").length + d.contratos.filter((c) => c.estado === "enviado").length;

  return (
    <PortalMarco empresa={d.empresa}>
      <div data-testid="portal-cliente" className="space-y-6">
        <EstadoObra obra={d.obra} cliente={d.cliente} />
        {pendientes > 0 && <p className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="portal-pendientes">Tienes {pendientes} documento{pendientes > 1 ? "s" : ""} pendiente{pendientes > 1 ? "s" : ""} de revisar.</p>}
        {secciones.length > 1 && (
          <nav className="sticky top-[64px] z-20 -mx-3 sm:mx-0 px-3 sm:px-0 py-2 bg-[#F4F7FA]/90 backdrop-blur flex gap-2 overflow-x-auto" data-testid="portal-nav">
            {secciones.map(([id, l, Icon, n]) => (
              <a key={id} href={`#${id}`} className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-[#1B2B4B] hover:border-[#00B4CC] transition-colors" data-testid={`portal-nav-${id}`}>
                <Icon className="w-3.5 h-3.5 text-[#00B4CC]" /> {l} <span className="text-slate-400">{n}</span>
              </a>
            ))}
          </nav>
        )}
        {d.presupuestos.length > 0 && <SeccionPortal id="presupuestos" icon={FileText} titulo="Presupuestos">
          <div className="space-y-3">{d.presupuestos.map((p) => <PresupuestoPortal key={p.id} p={p} onPdf={pdfPresupuesto} onResponder={(accion, x) => setResp({ accion, p: x })} />)}</div>
        </SeccionPortal>}
        {d.contratos.length > 0 && <SeccionPortal id="contratos" icon={FileSignature} titulo="Contratos"><ContratosPortal contratos={d.contratos} api={api} onFirmado={cargar} /></SeccionPortal>}
        {d.fotos.length > 0 && <SeccionPortal id="fotos" icon={Camera} titulo="Avance de la obra"><GaleriaPortal fotos={d.fotos} fotoUrl={api.fotoUrl} /></SeccionPortal>}
        {d.pagos.facturas.length > 0 && <SeccionPortal id="pagos" icon={Wallet} titulo="Pagos"><PagosPortal pagos={d.pagos} api={api} /></SeccionPortal>}
        {d.mensajes.length > 0 && <SeccionPortal id="mensajes" icon={MessageSquare} titulo="Mensajes del equipo"><MensajesPortal mensajes={d.mensajes} /></SeccionPortal>}
        {secciones.length === 0 && <p className="text-center text-sm text-slate-500 py-8" data-testid="portal-vacio">Aquí irán apareciendo los presupuestos, contratos, fotos y pagos de tu obra.</p>}
        <p className="text-center text-[11px] text-slate-400">Este enlace es personal e intransferible. Caduca el {new Date(d.expira).toLocaleDateString("es-ES")}.</p>
      </div>
      <ResponderPresupuesto accion={resp?.accion} presupuesto={resp?.p} onConfirmar={responder} onClose={() => setResp(null)} />
    </PortalMarco>
  );
}
